// File: src/hooks/useDetailViewLogic.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Custom hook for managing detail view edit mode state machine.
// Provides a standardized pattern for edit/save/cancel workflows.

import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Deep clone utility for proper data isolation
 * @param {any} obj - Object to clone
 * @returns {any} Deep cloned object
 */
const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    return JSON.parse(JSON.stringify(obj));
};

/**
 * useDetailViewLogic - Manages edit mode state machine for detail views
 *
 * @param {Object} options
 * @param {Object} options.initialData - Initial data for the form
 * @param {function} options.onSave - Async function to save data, receives formData
 * @param {function} [options.onValidate] - Optional validation function, returns { isValid, errors }
 * @param {function} [options.normalizeData] - Optional function to normalize data before editing
 * @param {function} [options.denormalizeData] - Optional function to denormalize data before saving
 * @returns {Object} State and actions for the detail view
 */
const useDetailViewLogic = ({
    initialData,
    onSave,
    onValidate,
    normalizeData,
    denormalizeData,
}) => {
    // Core state
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState(() => {
        const data = initialData || {};
        return normalizeData ? normalizeData(data) : deepClone(data);
    });
    const [originalData, setOriginalData] = useState(() => {
        const data = initialData || {};
        return normalizeData ? normalizeData(data) : deepClone(data);
    });

    // Message state
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Track if we're currently editing to prevent sync during edit
    const isEditingRef = useRef(isEditing);
    isEditingRef.current = isEditing;

    // Calculate hasUnsavedChanges
    const hasUnsavedChanges = isEditing && JSON.stringify(formData) !== JSON.stringify(originalData);

    // Sync formData when initialData changes (while not editing)
    useEffect(() => {
        if (!isEditingRef.current && initialData) {
            const data = normalizeData ? normalizeData(initialData) : deepClone(initialData);
            setFormData(data);
            setOriginalData(deepClone(data));
        }
    }, [initialData, normalizeData]);

    /**
     * Enter edit mode - deep clone current data as baseline for revert
     */
    const enterEditMode = useCallback(() => {
        // Store a deep clone of current form data as original for potential revert
        setOriginalData(deepClone(formData));
        setIsEditing(true);
        setError(null);
        setSuccess(null);
        setValidationErrors({});
    }, [formData]);

    /**
     * Exit edit mode - resets editing state without reverting data
     */
    const exitEditMode = useCallback(() => {
        setIsEditing(false);
        setValidationErrors({});
    }, []);

    /**
     * Handle save - validates and saves data
     * @returns {Promise<boolean>} Whether save was successful
     */
    const handleSave = useCallback(async () => {
        setError(null);
        setSuccess(null);
        setValidationErrors({});

        // Run validation if provided
        if (onValidate) {
            const validation = onValidate(formData);
            if (!validation.isValid) {
                setValidationErrors(validation.errors || {});
                setError('Please fix the validation errors before saving.');
                return false;
            }
        }

        setIsSaving(true);

        try {
            // Denormalize data if function provided
            const dataToSave = denormalizeData ? denormalizeData(formData) : formData;

            await onSave(dataToSave);

            // Update original data to match saved data
            setOriginalData(deepClone(formData));
            setIsEditing(false);
            setSuccess('Changes saved successfully!');
            return true;
        } catch (err) {
            const errorMessage = err?.message || 'An error occurred while saving.';
            setError(errorMessage);
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [formData, onSave, onValidate, denormalizeData]);

    /**
     * Handle cancel - reverts to original data and exits edit mode
     */
    const handleCancel = useCallback(() => {
        setFormData(deepClone(originalData));
        setIsEditing(false);
        setError(null);
        setValidationErrors({});
    }, [originalData]);

    /**
     * Update form data - merges partial updates into form data
     * @param {Object} updates - Partial object to merge into formData
     */
    const updateFormData = useCallback((updates) => {
        setFormData((prev) => ({
            ...prev,
            ...updates,
        }));
    }, []);

    /**
     * Set a single field value
     * @param {string} field - Field name (supports dot notation for nested fields)
     * @param {any} value - New value for the field
     */
    const setFieldValue = useCallback((field, value) => {
        setFormData((prev) => {
            // Handle nested fields with dot notation
            if (field.includes('.')) {
                const parts = field.split('.');
                const newData = deepClone(prev);
                let current = newData;

                for (let i = 0; i < parts.length - 1; i++) {
                    if (current[parts[i]] === undefined) {
                        current[parts[i]] = {};
                    }
                    current = current[parts[i]];
                }

                current[parts[parts.length - 1]] = value;
                return newData;
            }

            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    /**
     * Clear error and success messages
     */
    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);

    return {
        // State
        isEditing,
        isSaving,
        hasUnsavedChanges,
        formData,
        originalData,
        error,
        success,
        validationErrors,

        // Actions
        enterEditMode,
        exitEditMode,
        handleSave,
        handleCancel,
        updateFormData,
        setFieldValue,
        clearMessages,
    };
};

export default useDetailViewLogic;
